import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Button, Chip, CircularProgress, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Select, FormControl, InputLabel,
  IconButton, Tooltip, Avatar, Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  ConfirmationNumber as TicketIcon,
  Logout as LogoutIcon,
  SupportAgent as SupportIcon,
  OpenInNew as OpenIcon,
} from '@mui/icons-material';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { usePortalAuthStore } from '../../stores/portalAuthStore';
import { useBranding } from '../../utils/BrandingContext';
import api, { extractData } from '../../utils/api';

const statusColors: Record<string, string> = {
  open: '#3b82f6', in_progress: '#f59e0b', resolved: '#10b981', closed: '#6b7280', on_hold: '#8b5cf6',
};
const statusLabels: Record<string, string> = {
  open: 'Open', in_progress: 'In Progress', resolved: 'Resolved', closed: 'Closed', on_hold: 'On Hold',
};
const priorityColors: Record<string, string> = {
  urgent: '#ef4444', high: '#f97316', medium: '#eab308', low: '#22c55e',
};

interface Ticket {
  id: string;
  ticketNumber: string;
  subject: string;
  status: string;
  priority: string;
  category: string;
  createdAt: string;
  updatedAt: string;
}

const HelpdeskPortalTickets: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = usePortalAuthStore();
  const { branding } = useBranding();
  const primaryColor = branding.primaryColor || '#6366f1';

  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // New ticket dialog
  const [newTicketOpen, setNewTicketOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('general');
  const [priority, setPriority] = useState('medium');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [categories, setCategories] = useState<string[]>(['billing', 'technical', 'account', 'general']);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const res = await api.get('/support/portal/tickets');
      const data = extractData(res) as any;
      setTickets(data?.tickets || []);
      setError('');
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const fetchConfig = async () => {
    try {
      const res = await api.get('/support/helpdesk/config', {
        headers: { 'x-helpdesk-domain': window.location.hostname },
      });
      const cfg = extractData(res) as any;
      if (cfg?.categories?.length) setCategories(cfg.categories);
    } catch {}
  };

  useEffect(() => {
    fetchTickets();
    fetchConfig();
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/portal/login');
  };

  const handleSubmitTicket = async () => {
    if (!subject.trim() || !description.trim()) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      await api.post('/support/portal/tickets', { subject, description, category, priority });
      setNewTicketOpen(false);
      setSubject('');
      setDescription('');
      setCategory('general');
      setPriority('medium');
      fetchTickets();
    } catch (e: any) {
      setSubmitError(e?.response?.data?.message || 'Failed to submit ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const getInitials = (u: typeof user) => {
    if (!u) return 'U';
    if (u.firstName) return (u.firstName[0] + (u.lastName?.[0] || '')).toUpperCase();
    return u.email[0].toUpperCase();
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f0f2f5', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box
        sx={{
          bgcolor: '#fff', borderBottom: '1px solid #e5e7eb',
          px: 3, py: 1.5, display: 'flex', alignItems: 'center', gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {branding.logo ? (
            <Box component="img" src={branding.logo} alt="" sx={{ height: 32, objectFit: 'contain' }} />
          ) : (
            <Box sx={{ width: 32, height: 32, borderRadius: '8px', bgcolor: primaryColor, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <SupportIcon sx={{ color: '#fff', fontSize: 18 }} />
            </Box>
          )}
          <Typography fontWeight={700} fontSize="1rem" color="#1f2937">
            {branding.companyName || 'Support'} — My Tickets
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
          {user?.email}
        </Typography>
        <Avatar sx={{ width: 32, height: 32, bgcolor: primaryColor, fontSize: '0.8rem', fontWeight: 700 }}>
          {getInitials(user)}
        </Avatar>
        <Tooltip title="Sign out">
          <IconButton size="small" onClick={handleLogout} sx={{ color: '#9ca3af' }}>
            <LogoutIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, maxWidth: 860, width: '100%', mx: 'auto', px: 2, py: 3 }}>
        {/* Toolbar */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
          <Typography variant="h6" fontWeight={700} color="#1f2937" sx={{ flex: 1 }}>
            Support Tickets
          </Typography>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={fetchTickets} sx={{ color: '#6b7280' }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setNewTicketOpen(true)}
            sx={{
              textTransform: 'none', fontWeight: 600, borderRadius: '8px',
              bgcolor: primaryColor, '&:hover': { bgcolor: primaryColor, filter: 'brightness(0.9)' },
            }}
          >
            New Ticket
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <CircularProgress size={32} sx={{ color: primaryColor }} />
          </Box>
        ) : tickets.length === 0 ? (
          <Paper
            elevation={0}
            sx={{ p: 6, textAlign: 'center', borderRadius: '12px', border: '1px solid #e5e7eb' }}
          >
            <TicketIcon sx={{ fontSize: 48, color: '#d1d5db', mb: 2 }} />
            <Typography fontWeight={600} color="#374151" mb={0.5}>No tickets yet</Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Submit a ticket and we'll get back to you quickly.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setNewTicketOpen(true)}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', bgcolor: primaryColor }}
            >
              Submit your first ticket
            </Button>
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {tickets.map(ticket => (
              <Paper
                key={ticket.id}
                elevation={0}
                sx={{
                  p: 2.5, borderRadius: '12px', border: '1px solid #e5e7eb',
                  cursor: 'pointer', transition: 'box-shadow 0.15s',
                  '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderColor: '#d1d5db' },
                }}
                onClick={() => navigate(`/portal/tickets/${ticket.id}`)}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                      <Typography variant="caption" sx={{ color: '#9ca3af', fontFamily: 'monospace', fontWeight: 600 }}>
                        #{ticket.ticketNumber}
                      </Typography>
                      <Chip
                        label={statusLabels[ticket.status] || ticket.status}
                        size="small"
                        sx={{
                          height: 20, fontSize: '0.65rem', fontWeight: 600,
                          bgcolor: `${statusColors[ticket.status]}22`,
                          color: statusColors[ticket.status] || '#6b7280',
                        }}
                      />
                      {ticket.priority !== 'medium' && (
                        <Box
                          sx={{
                            width: 8, height: 8, borderRadius: '50%',
                            bgcolor: priorityColors[ticket.priority] || '#9ca3af',
                          }}
                        />
                      )}
                    </Box>
                    <Typography fontWeight={600} fontSize="0.95rem" color="#1f2937" mb={0.25}>
                      {ticket.subject}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {ticket.category && (
                        <span style={{ textTransform: 'capitalize', marginRight: 8 }}>{ticket.category}</span>
                      )}
                      Updated {formatDistanceToNow(parseISO(ticket.updatedAt), { addSuffix: true })}
                    </Typography>
                  </Box>
                  <OpenIcon sx={{ fontSize: 16, color: '#d1d5db', flexShrink: 0, mt: 0.5 }} />
                </Box>
              </Paper>
            ))}
          </Box>
        )}
      </Box>

      {/* New Ticket Dialog */}
      <Dialog
        open={newTicketOpen}
        onClose={() => !submitting && setNewTicketOpen(false)}
        fullWidth maxWidth="sm"
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Submit a New Ticket</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2.5 }}>
          {submitError && <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert>}

          <TextField
            label="Subject"
            fullWidth size="small"
            value={subject}
            onChange={e => setSubject(e.target.value)}
            required sx={{ mb: 2 }}
            placeholder="Brief summary of your issue"
          />

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Category</InputLabel>
              <Select value={category} onChange={e => setCategory(e.target.value)} label="Category">
                {categories.map(c => (
                  <MenuItem key={c} value={c} sx={{ textTransform: 'capitalize' }}>{c}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select value={priority} onChange={e => setPriority(e.target.value)} label="Priority">
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="urgent">Urgent</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <TextField
            label="Description"
            fullWidth multiline rows={5} size="small"
            value={description}
            onChange={e => setDescription(e.target.value)}
            required
            placeholder="Please describe your issue in detail..."
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setNewTicketOpen(false)} disabled={submitting} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitTicket}
            disabled={submitting || !subject.trim() || !description.trim()}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', bgcolor: primaryColor }}
          >
            {submitting ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Submit Ticket'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HelpdeskPortalTickets;
